import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

const CALL_REASONS = {
  'Google Review Agent': 'Only has a small number of Google reviews — automated review system could significantly lift their local search ranking.',
  'Website Development': 'No website or weak online presence — missing out on leads that search online for their service.',
  'Chatbot (Carl)': 'Has a website but no live chat — losing enquiries that come in outside business hours.',
  'Automated Admin Assistant': 'Admin-heavy business — repetitive follow-ups and reminders are eating into their time.',
};

function getCallReason(serviceGap) {
  if (!serviceGap) return 'Could benefit from automation.';
  for (const [key, reason] of Object.entries(CALL_REASONS)) {
    if (serviceGap.includes(key)) return reason;
  }
  return CALL_REASONS[serviceGap] || 'Could benefit from our services.';
}

export default function SetterExport() {
  const [prospects, setProspects] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calledToggles, setCalledToggles] = useState({});

  const load = useCallback(() => {
    api.getProspects()
      .then(data => {
        const active = (Array.isArray(data) ? data : []).filter(p =>
          p.status === 'New' || p.status === 'Emailed' || p.status === 'Follow Up'
        );
        setProspects(active);
        // Init called toggles from current data
        const toggleMap = {};
        active.forEach(p => { toggleMap[p.id] = p.called || false; });
        setCalledToggles(toggleMap);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(prospects.map(p => p.id)));
  };

  const selectHotLeads = () => {
    setSelected(new Set(
      prospects
        .filter(p => p.isHot || (p.reviewCount !== undefined && p.reviewCount < 30))
        .map(p => p.id)
    ));
  };

  const clearSelection = () => setSelected(new Set());

  const handleToggleCalled = async (p) => {
    const newVal = !calledToggles[p.id];
    setCalledToggles(prev => ({ ...prev, [p.id]: newVal }));
    try {
      await api.updateProspect(p.id, { called: newVal });
    } catch (e) {
      // column may not exist — revert
      setCalledToggles(prev => ({ ...prev, [p.id]: !newVal }));
    }
  };

  const selectedProspects = prospects.filter(p => selected.has(p.id));

  const formatForCopy = () => {
    return selectedProspects.map(p => {
      const reason = getCallReason(p.serviceGap);
      const emailed = (p.lastEmailedAt || p.status === 'Emailed' || p.status === 'Follow Up') ? 'Yes' : 'No';
      return `Business: ${p.businessName}
Phone: ${p.phone || 'No phone'}
Service: ${p.serviceGap || '—'}
Reason: ${reason}
Emailed: ${emailed}
---`;
    }).join('\n');
  };

  const handleCopy = () => {
    const text = formatForCopy();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleExportCSV = () => {
    const headers = ['Business Name', 'Phone', 'Email', 'Service Gap', 'Reason for Calling', 'Status', 'Emailed', 'Called'];
    const rows = selectedProspects.map(p => [
      `"${(p.businessName || '').replace(/"/g, '""')}"`,
      `"${(p.phone || '').replace(/"/g, '""')}"`,
      `"${(p.email || '').replace(/"/g, '""')}"`,
      `"${(p.serviceGap || '').replace(/"/g, '""')}"`,
      `"${(getCallReason(p.serviceGap) || '').replace(/"/g, '""')}"`,
      `"${(p.status || '').replace(/"/g, '""')}"`,
      p.lastEmailedAt ? 'Yes' : 'No',
      calledToggles[p.id] ? 'Yes' : 'No',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `setter-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-gray-400">Loading setter export...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Setter Export</h2>
        <span className="text-sm text-gray-500">{prospects.length} active prospects</span>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-3 mb-4 bg-gray-900 border border-gray-800 rounded-xl p-4">
        <button
          onClick={selectAll}
          className="text-sm px-4 py-2 bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/30 transition-colors"
        >
          Select All ({prospects.length})
        </button>
        <button
          onClick={selectHotLeads}
          className="text-sm px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
        >
          Select Hot Leads
        </button>
        {selected.size > 0 && (
          <>
            <button
              onClick={clearSelection}
              className="text-sm px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Clear ({selected.size})
            </button>
            <button
              onClick={handleCopy}
              className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors ${
                copied ? 'bg-green-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              }`}
            >
              {copied ? 'Copied!' : `Copy Selected (${selected.size})`}
            </button>
            <button
              onClick={handleExportCSV}
              className="text-sm px-4 py-2 bg-emerald-600/20 text-emerald-400 rounded-lg hover:bg-emerald-600/30 transition-colors"
            >
              Export CSV
            </button>
          </>
        )}
      </div>

      {/* Preview of selected */}
      {selected.size > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-5">
          <p className="text-xs text-gray-400 mb-2">Copy preview ({selected.size} prospects):</p>
          <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
            {formatForCopy()}
          </pre>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-900">
            <tr className="text-left text-gray-400">
              <th className="px-4 py-3 font-medium w-10">
                <input
                  type="checkbox"
                  checked={selected.size === prospects.length && prospects.length > 0}
                  onChange={e => e.target.checked ? selectAll() : clearSelection()}
                  className="w-4 h-4"
                />
              </th>
              <th className="px-4 py-3 font-medium">Business</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Service</th>
              <th className="px-4 py-3 font-medium">Reason for Calling</th>
              <th className="px-4 py-3 font-medium">Emailed</th>
              <th className="px-4 py-3 font-medium">Called</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {prospects.map(p => (
              <tr key={p.id} className={`hover:bg-gray-900/50 ${selected.has(p.id) ? 'bg-blue-900/10' : ''}`}>
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    className="w-4 h-4"
                  />
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium">{p.businessName}</p>
                  {p.ownerName && <p className="text-xs text-gray-500">{p.ownerName}</p>}
                  {p.isHot && <span className="text-xs text-red-400">&#x25cf; Hot</span>}
                </td>
                <td className="px-4 py-3 text-gray-300">{p.phone || <span className="text-gray-600">—</span>}</td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400">{p.serviceGap || '—'}</span>
                </td>
                <td className="px-4 py-3">
                  <p className="text-xs text-gray-400 max-w-[250px]">{getCallReason(p.serviceGap)}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    p.lastEmailedAt ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-500'
                  }`}>
                    {p.lastEmailedAt ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleCalled(p)}
                    className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                      calledToggles[p.id]
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        : 'bg-gray-800 text-gray-500 hover:bg-gray-700 hover:text-gray-300'
                    }`}
                  >
                    {calledToggles[p.id] ? '&#9742; Called' : '&#9742; Not called'}
                  </button>
                </td>
              </tr>
            ))}
            {prospects.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No active prospects.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
